import React, { useState, useEffect } from 'react';
import {
    Button,
    Header,
    Icon,
    List,
    Modal,
    Image,
    Grid,
    Segment,
    Transition
} from 'semantic-ui-react';

/**
 * A modal component that encourages users to subscribe to premium features
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {Function} props.onContinue - Function to call when the user clicks "Continue with Limited Access"
 * @param {boolean} props.isSecondaryThreshold - Whether this modal is showing for the secondary threshold
 * @param {boolean} props.isTertiaryThreshold - Whether this modal is showing for the tertiary threshold
 * @param {string} props.current_locale - The current locale of the application
 * @param {boolean} props.showStudentNote - Whether to show the student note section (default: false)
 * @returns {JSX.Element} The subscription modal component
 */
const SubscriptionModal = ({ open, cheapest_plan, onClose, onContinue, isSecondaryThreshold, isTertiaryThreshold, current_locale = 'en', showStudentNote = false }) => {
    const [shimmerIndex, setShimmerIndex] = useState(0);
    
    // Localization data for all supported languages
    const translations = {
        en: {
            upgradeExperience: 'Upgrade Your Experience',
            premiumBenefits: 'Benefits',
            unlimitedLabTime: 'Unlimited Lab Time',
            noTimeRestrictions: 'No time restrictions',
            saveUnlimitedCircuits: 'Save your experiments',
            neverLoseWork: 'Never lose your work',
            allComponents: '40+ Components',
            fullComponentLibrary: 'Real-looking parts',
            prioritySupport: 'Priority Support',
            helpWhenNeeded: 'Help when you need it',
            testimonial: 'It\'s enjoyable to interact with. The user interface gives me the sensation of operating a real Relay unit in a laboratory.',
            testimonialAuthor: 'Hobbyist in Queensland',
            continueWithLimited: 'Continue with Limited Access',
            upgradeNow: 'Start with {cheapest_plan}!',
            schoolLicenseMessage: 'We kindly ask students to check with their teachers about obtaining a school license to continue using DCACLab.'
        },
        ar: {
            upgradeExperience: 'ترقية تجربتك',
            premiumBenefits: 'مزايا',
            unlimitedLabTime: 'وقت مختبر غير محدود',
            noTimeRestrictions: 'لا قيود زمنية',
            saveUnlimitedCircuits: 'احفظ تجاربك',
            neverLoseWork: 'لن تفقد عملك أبدًا',
            allComponents: '40+ مكونات',
            fullComponentLibrary: 'قطع ذات مظهر حقيقي',
            prioritySupport: 'دعم ذو أولوية',
            helpWhenNeeded: 'المساعدة عند الحاجة',
            testimonial: 'من الممتع التفاعل معه. واجهة المستخدم تعطيني إحساس بتشغيل وحدة مرحل حقيقية في المختبر.',
            testimonialAuthor: 'هاوٍ في كوينزلاند',
            continueWithLimited: 'المتابعة بوصول محدود',
            upgradeNow: 'ابدأ مع {cheapest_plan}!',
            schoolLicenseMessage: 'نطلب بلطف من الطلاب التحقق مع معلميهم بشأن الحصول على ترخيص مدرسي لمواصلة استخدام DCACLab.'
        },
        de: {
            upgradeExperience: 'Verbessern Sie Ihr Erlebnis',
            premiumBenefits: 'Vorteile',
            unlimitedLabTime: 'Unbegrenzte Laborzeit',
            noTimeRestrictions: 'Keine Zeitbeschränkungen',
            saveUnlimitedCircuits: 'Speichern Sie Ihre Experimente',
            neverLoseWork: 'Verlieren Sie nie Ihre Arbeit',
            allComponents: '40+ Komponenten',
            fullComponentLibrary: 'Realistisch aussehende Teile',
            prioritySupport: 'Prioritäts-Support',
            helpWhenNeeded: 'Hilfe, wenn Sie sie brauchen',
            testimonial: 'Es macht Spaß, damit zu interagieren. Die Benutzeroberfläche gibt mir das Gefühl, ein echtes Relais in einem Labor zu bedienen.',
            testimonialAuthor: 'Hobbyist in Queensland',
            continueWithLimited: 'Mit eingeschränktem Zugriff fortfahren',
            upgradeNow: 'Starten Sie mit {cheapest_plan}!',
            schoolLicenseMessage: 'Wir bitten Schüler höflich, mit ihren Lehrern über den Erwerb einer Schullizenz zu sprechen, um DCACLab weiterhin nutzen zu können.'
        },
        fr: {
            upgradeExperience: 'Améliorez Votre Expérience',
            premiumBenefits: 'Avantages',
            unlimitedLabTime: 'Temps de Laboratoire Illimité',
            noTimeRestrictions: 'Pas de restrictions de temps',
            saveUnlimitedCircuits: 'Sauvegardez vos expériences',
            neverLoseWork: 'Ne perdez jamais votre travail',
            allComponents: '40+ Composants',
            fullComponentLibrary: 'Pièces d\'apparence réaliste',
            prioritySupport: 'Support Prioritaire',
            helpWhenNeeded: 'De l\'aide quand vous en avez besoin',
            testimonial: 'C\'est agréable d\'interagir avec. L\'interface utilisateur me donne la sensation de faire fonctionner une unité de relais réelle dans un laboratoire.',
            testimonialAuthor: 'Passionné au Queensland',
            continueWithLimited: 'Continuer avec un Accès Limité',
            upgradeNow: 'Commencez avec {cheapest_plan}!',
            schoolLicenseMessage: 'Nous demandons gentiment aux étudiants de se renseigner auprès de leurs enseignants concernant l\'obtention d\'une licence scolaire pour continuer à utiliser DCACLab.'
        },
        es: {
            upgradeExperience: 'Mejora Tu Experiencia',
            premiumBenefits: 'Beneficios',
            unlimitedLabTime: 'Tiempo de Laboratorio Ilimitado',
            noTimeRestrictions: 'Sin restricciones de tiempo',
            saveUnlimitedCircuits: 'Guarda tus experimentos',
            neverLoseWork: 'Nunca pierdas tu trabajo',
            allComponents: '40+ Componentes',
            fullComponentLibrary: 'Partes de aspecto real',
            prioritySupport: 'Soporte Prioritario',
            helpWhenNeeded: 'Ayuda cuando la necesites',
            testimonial: 'Es agradable interactuar con él. La interfaz de usuario me da la sensación de operar una unidad de relé real en un laboratorio.',
            testimonialAuthor: 'Aficionado en Queensland',
            continueWithLimited: 'Continuar con Acceso Limitado',
            upgradeNow: '¡Empezar con {cheapest_plan}!',
            schoolLicenseMessage: 'Para seguir usando DCACLab, los estudiantes necesitan una licencia escolar. Por favor, pide a tu profesor que compre una para acceso ilimitado.'
        },
        tr: {
            upgradeExperience: 'Deneyiminizi Yükseltin',
            premiumBenefits: 'Avantajlar',
            unlimitedLabTime: 'Sınırsız Laboratuvar Süresi',
            noTimeRestrictions: 'Zaman kısıtlaması yok',
            saveUnlimitedCircuits: 'Deneyimlerinizi kaydedin',
            neverLoseWork: 'Çalışmalarınızı asla kaybetmeyin',
            allComponents: '40+ Bileşen',
            fullComponentLibrary: 'Gerçek görünümlü parçalar',
            prioritySupport: 'Öncelikli Destek',
            helpWhenNeeded: 'İhtiyacınız olduğunda yardım',
            testimonial: 'Onunla etkileşim kurmak keyifli. Kullanıcı arabirimi bana laboratuvarda gerçek bir röle birimi çalıştırdığım hissini veriyor.',
            testimonialAuthor: 'Queensland\'deki Hobi Meraklısı',
            continueWithLimited: 'Sınırlı Erişimle Devam Et',
            upgradeNow: '{cheapest_plan} ile başlayın!',
            schoolLicenseMessage: 'Öğrencilerden DCACLab\'u kullanmaya devam etmek için öğretmenleriyle okul lisansı alma konusunda görüşmelerini nazikce rica ediyoruz.'
        },
        th: {
            upgradeExperience: 'อัปเกรดประสบการณ์ของคุณ',
            premiumBenefits: 'สิทธิประโยชน์',
            unlimitedLabTime: 'เวลาห้องแล็บไม่จำกัด',
            noTimeRestrictions: 'ไม่มีข้อจำกัดด้านเวลา',
            saveUnlimitedCircuits: 'บันทึกการทดลองของคุณ',
            neverLoseWork: 'ไม่สูญเสียงานของคุณ',
            allComponents: '40+ ส่วนประกอบ',
            fullComponentLibrary: 'ชิ้นส่วนที่ดูจริง',
            prioritySupport: 'การสนับสนุนที่มีความสำคัญ',
            helpWhenNeeded: 'ช่วยเหลือเมื่อคุณต้องการ',
            testimonial: 'มันสนุกที่จะโต้ตอบด้วย อินเทอร์เฟซให้ความรู้สึกเหมือนการใช้งานหน่วยรีเลย์จริงในห้องแล็บ',
            testimonialAuthor: 'นักสะสมงานอดิเรกในควีนส์แลนด์',
            continueWithLimited: 'ดำเนินการต่อด้วยการเข้าถึงแบบจำกัด',
            upgradeNow: 'เริ่มต้นด้วย {cheapest_plan}!',
            schoolLicenseMessage: 'เราขอเรียนเชิญให้นักเรียนตรวจสอบกับครูผู้สอนของพวกเขาเกี่ยวกับการขอรับใบอนุญาตสำหรับโรงเรียนเพื่อใช้ DCACLab ต่อไป'
        },
        mx: {
            upgradeExperience: 'Mejora Tu Experiencia',
            premiumBenefits: 'Beneficios',
            unlimitedLabTime: 'Tiempo de Laboratorio Ilimitado',
            noTimeRestrictions: 'Sin restricciones de tiempo',
            saveUnlimitedCircuits: 'Guarda tus experimentos',
            neverLoseWork: 'Nunca pierdas tu trabajo',
            allComponents: '40+ Componentes',
            fullComponentLibrary: 'Partes de aspecto real',
            prioritySupport: 'Soporte Prioritario',
            helpWhenNeeded: 'Ayuda cuando la necesites',
            testimonial: 'Es agradable interactuar con él. La interfaz de usuario me da la sensación de operar una unidad de relé real en un laboratorio.',
            testimonialAuthor: 'Aficionado en Queensland',
            continueWithLimited: 'Continuar con Acceso Limitado',
            upgradeNow: '¡Empezar con {cheapest_plan}!',
            schoolLicenseMessage: 'Para seguir usando DCACLab, los estudiantes necesitan una licencia escolar. Por favor, pide a tu profesor que compre una para acceso ilimitado.'
        }
    };
    
    // Get the correct translations based on locale, fallback to English
    const t = translations[current_locale] || translations['en'];
    
    // Check if the current locale is RTL (Right-to-Left)
    const isRTL = current_locale === 'ar';
    // Features to highlight
    const features = [
        { icon: 'clock', title: t.unlimitedLabTime, description: t.noTimeRestrictions },
        { icon: 'save', title: t.saveUnlimitedCircuits, description: t.neverLoseWork },
        { icon: 'microchip', title: t.allComponents, description: t.fullComponentLibrary },
        { icon: 'headphones', title: t.prioritySupport, description: t.helpWhenNeeded }
    ];
    
    // Handle continue with limited access
    const handleContinue = () => {
        if (onContinue) {
            onContinue();
        }
        onClose();
    };
    
    // Handle modal close
    const handleClose = () => {
        onClose();
    };

    // Glow effect for benefits - cycle through buttons in round robin
    useEffect(() => {
        if (open) {
            const interval = setInterval(() => {
                setShimmerIndex(prevIndex => (prevIndex + 1) % features.length);
            }, 2000); // Change every 2 seconds

            return () => clearInterval(interval);
        }
    }, [open, features.length]);
    return (
        <>
            <style>{`
                @keyframes shimmer {
                    0% {
                        transform: translateX(-100%);
                    }
                    100% {
                        transform: translateX(300%);
                    }
                }
                
                @keyframes benefitGlow {
                    0% {
                        border-color: #e0e0e0;
                        box-shadow: 0 0 0 0 rgba(242, 113, 28, 0);
                    }
                    50% {
                        border-color: rgba(242, 113, 28, 0.7);
                        box-shadow: 0 0 0 3px rgba(242, 113, 28, 0.2);
                    }
                    100% {
                        border-color: #e0e0e0;
                        box-shadow: 0 0 0 0 rgba(242, 113, 28, 0);
                    }
                }
                
                .benefit-glow-active {
                    animation: benefitGlow 2s ease-in-out infinite;
                }
            `}</style>
            <Modal
            open={open}
            dimmer="inverted"
            size="tiny"
            className={`subscription-modal ${isRTL ? 'rtl' : ''}`}
            closeOnDimmerClick={!isSecondaryThreshold}
            onClose={handleClose}
            style={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 12px 28px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '90vh'
            }}
        >
            <Modal.Header style={{ 
                background: 'linear-gradient(135deg, #f2711c 0%, #e67e22 100%)',
                color: 'white',
                padding: '1.2em',
                borderBottom: 'none'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <Icon name="star" size="large" style={{ color: '#FFD700', marginRight: isRTL ? '0' : '10px', marginLeft: isRTL ? '10px' : '0' }} />
                    <span style={{ fontSize: '1.3em', fontWeight: '600' }}>{t.upgradeExperience}</span>
                    
                </div>
            </Modal.Header>
            <Modal.Content style={{ padding: '0', maxHeight: '60vh', overflowY: 'auto' }}>
                {/* Feature Highlight Section - More compact */}
                <div style={{ padding: '15px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <Header as="h3" style={{ 
                        color: '#f2711c', 
                        fontSize: '16px',
                        margin: '0 0 10px',
                        fontWeight: '600',
                        textAlign: 'center'
                    }}>
                        {t.premiumBenefits}
                    </Header>
                    
                    <Grid stackable columns={2} style={{ margin: '0' }}>
                        {features.map((feature, index) => (
                            <Grid.Column key={index} style={{ padding: '5px' }}>
                                <Segment 
                                    className={shimmerIndex === index ? 'benefit-glow-active' : ''}
                                    style={{ 
                                        padding: '10px', 
                                        height: '100%',
                                        borderRadius: '8px',
                                        border: '1px solid #e0e0e0',
                                        background: 'white',
                                        transition: 'all 0.3s ease',
                                        direction: isRTL ? 'rtl' : 'ltr',
                                        textAlign: isRTL ? 'right' : 'left'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row', width: '100%', justifyContent: 'flex-start' }}>
                                        <Icon 
                                            name={feature.icon} 
                                            style={{ 
                                                color: '#f2711c',
                                                marginRight: isRTL ? '0' : '8px',
                                                marginLeft: isRTL ? '8px' : '0'
                                            }} 
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333', textAlign: isRTL ? 'right' : 'left' }}>
                                                {feature.title}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666', textAlign: isRTL ? 'right' : 'left' }}>
                                                {feature.description}
                                            </div>
                                        </div>
                                    </div>
                                </Segment>
                            </Grid.Column>
                        ))}
                    </Grid>
                    
                    {/* Testimonial/Social Proof - More compact */}
                    <div style={{ 
                        background: '#f9f9f9', 
                        borderRadius: '8px',
                        padding: '10px',
                        margin: '20px 0 0',
                        display: 'flex',
                        alignItems: 'center',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                    }}>
                        <div style={{ 
                            minWidth: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            background: '#e0e1e2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: isRTL ? '0' : '10px',
                            marginLeft: isRTL ? '10px' : '0'
                        }}>
                            <Icon name="user" size="small" style={{ margin: 0 }} />
                        </div>
                        <div>
                            <p style={{ 
                                fontSize: '12px', 
                                fontStyle: 'italic',
                                margin: '0 0 2px',
                                color: '#555'
                            }}>
                                "{t.testimonial}"
                            </p>
                            <p style={{ fontSize: '11px', margin: 0, color: '#777' }}>{isRTL ? `${t.testimonialAuthor} —` : `— ${t.testimonialAuthor}`}</p>
                        </div>
                    </div>
                    
                    {/* Student Call to Action for School License */}
                    {showStudentNote && (
                        <div style={{ 
                            background: '#e6f7ff', 
                            borderRadius: '8px',
                            padding: '10px',
                            margin: '10px 0 0',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #91d5ff',
                            direction: isRTL ? 'rtl' : 'ltr',
                            textAlign: isRTL ? 'right' : 'left',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Shimmer animation effect */}
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.6) 50%, transparent 100%)',
                                borderRadius: '12px',
                                animation: 'shimmer 3s ease-in-out infinite',
                                pointerEvents: 'none'
                            }}></div>
                            <div style={{ 
                                minWidth: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                background: '#1890ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: isRTL ? '0' : '10px',
                                marginLeft: isRTL ? '10px' : '0',
                                color: 'white'
                            }}>
                                <Icon name="student" size="small" style={{ margin: 0 }} />
                            </div>
                            <div>
                                <p style={{ 
                                    fontSize: '13px', 
                                    fontWeight: 'bold',
                                    margin: '0 0 2px',
                                    color: '#0050b3'
                                }}>
                                    {t.schoolLicenseMessage}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Content>
            <Modal.Actions style={{ 
                background: '#f8f9fa',
                borderTop: '1px solid #eee',
                padding: '12px 15px',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                position: 'sticky',
                bottom: 0,
                direction: isRTL ? 'rtl' : 'ltr'
            }}>
                {!isTertiaryThreshold && (
                    <Button
                        style={{
                            marginLeft: isRTL ? '0' : '5px',
                            marginRight: isRTL ? '5px' : '0',
                            margin: '5px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                            color: '#444',
                            fontWeight: '500',
                            background: '#f0f0f0',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '10px 16px',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                        className="limited-access-button"
                        onClick={handleContinue}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#e8e8e8';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f0f0f0';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 5px rgba(0,0,0,0.15)';
                        }}
                    >
                        {t.continueWithLimited}
                    </Button>
                )}
                <Button
                    style={{
                        margin: '5px',
                        background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                        color: 'white',
                        padding: '12px 20px',
                        fontWeight: '600',
                        borderRadius: '6px',
                        boxShadow: '0 4px 10px rgba(33, 133, 208, 0.3)',
                        transition: 'all 0.2s ease'
                    }}
                    icon
                    labelPosition="right"
                    onClick={() => {
                        window.open(`/${current_locale}/pricing-table`, '_blank');
                        handleClose();
                    }}
                >
                    {cheapest_plan ? t.upgradeNow.replace('{cheapest_plan}', cheapest_plan) : 'Upgrade Now'}
                    <Icon name="arrow right" />
                </Button>
            </Modal.Actions>
        </Modal>
        </>
    );
};

export default SubscriptionModal;